import { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useAuthStore, useGuestStore, useRoomStore, useWebSocketStore } from '@/stores'
import { RoomSidebar } from '@/components/chat/RoomSidebar'
import { MessageList } from '@/components/chat/MessageList'
import MessageInput from '@/components/chat/MessageInput'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Grid3x3, ExternalLink, UserCog, Users, MessageSquare, LogOut, Circle } from 'lucide-react'
import { Identicon } from '@/components/Identicon'
import { config } from '@/lib/config'
import { useNavigate } from 'react-router-dom'

const POLLING_INTERVAL_MS = 10000

const hogeSeriesProducts = [
  {
    name: 'hogeAdmin',
    url: import.meta.env.VITE_HOGE_ADMIN_URL,
    icon: UserCog,
    description: 'テナント管理',
    current: false,
  },
  {
    name: 'hogeCast',
    url: `${import.meta.env.VITE_HOGE_CAST_URL}?spoofingTenantId=${''}`,
    icon: MessageSquare,
    description: 'セグメント配信',
    current: false,
  },
  {
    name: 'hogeAgent',
    url: import.meta.env.VITE_HOGE_AGENT_URL,
    icon: Users,
    description: 'リアルタイムチャット',
    current: true,
  },
]

const ChatPage = observer(() => {
  const authStore = useAuthStore()
  const roomStore = useRoomStore()
  const guestStore = useGuestStore()
  const webSocketStore = useWebSocketStore()
  const navigate = useNavigate()

  const [sending, setSending] = useState(false)
  const [uploading, setUploading] = useState(false)

  const tenantId = authStore.currentTenantId
  const userId = authStore.user?.id || ''

  const currentRoom = roomStore.currentRoom

  // initial load
  useEffect(() => {
    if (!tenantId || !userId) return

    void roomStore.fetchRooms()

    // realtime connection
    webSocketStore.connect({ x: 'user', t: tenantId, u: userId })

    return () => {
      webSocketStore.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, userId])

  // polling fallback when realtime is disabled or disconnected
  useEffect(() => {
    if (webSocketStore.isEnabled && webSocketStore.status === 'connected') return

    const timer = setInterval(() => {
      void roomStore.fetchRooms()
      if (roomStore.currentRoomId) {
        void roomStore.fetchRoomDetail(roomStore.currentRoomId)
      }
    }, POLLING_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [webSocketStore.isEnabled, webSocketStore.status, roomStore])

  const handleSelectRoom = useCallback(
    async (roomId: string) => {
      roomStore.setCurrentRoom(roomId)
      await roomStore.fetchRoomDetail(roomId)
    },
    [roomStore]
  )

  const handleAssign = async () => {
    if (!currentRoom) return
    const success = await roomStore.assignRoom(currentRoom.id)
    if (success) await roomStore.fetchRoomDetail(currentRoom.id)
  }

  const handleRelease = async () => {
    if (!currentRoom) return
    await roomStore.releaseRoom(currentRoom.id)
  }

  const handleClose = async () => {
    if (!currentRoom) return
    const success = await roomStore.closeRoom(currentRoom.id)
    if (success) roomStore.setCurrentRoom('')
  }

  const handleSend = async (text: string) => {
    if (!currentRoom) return

    setSending(true)
    try {
      const sent = webSocketStore.send(
        JSON.stringify({
          type: 'message',
          data: {
            roomId: currentRoom.id,
            text,
            messageType: 'text',
            imageUrl: '',
          },
        })
      )

      if (!sent) {
        authStore.tryShowToast('リアルタイム接続がありません', 'error')
        return
      }

      // optimistic append; the WS echo will be deduped by id on refetch
      setTimeout(() => {
        if (roomStore.currentRoomId === currentRoom.id) {
          void roomStore.fetchRoomDetail(currentRoom.id).catch(() => undefined)
        }
      }, 800)
    } finally {
      setSending(false)
    }
  }

  const handleImageSelected = async (file: File) => {
    if (!currentRoom) return

    setUploading(true)
    try {
      const imageUrl = await roomStore.uploadImage(currentRoom.id, file)
      if (!imageUrl) return

      const sent = webSocketStore.send(
        JSON.stringify({
          type: 'message',
          data: {
            roomId: currentRoom.id,
            text: '',
            messageType: 'image',
            imageUrl,
          },
        })
      )
      if (!sent) {
        authStore.tryShowToast('リアルタイム接続がありません', 'error')
        return
      }

      setTimeout(() => {
        if (roomStore.currentRoomId === currentRoom.id) {
          void roomStore.fetchRoomDetail(currentRoom.id).catch(() => undefined)
        }
      }, 1200)
    } finally {
      setUploading(false)
    }
  }

  // resolve guest name for the header
  const guestId = currentRoom?.guest
  useEffect(() => {
    if (guestId && tenantId && !guestStore.guests.has(guestId)) {
      void guestStore.getGuest(tenantId, guestId)
    }
  }, [guestId, tenantId, guestStore])

  const guestName = useMemo(() => {
    if (!guestId) return ''
    return guestStore.guests.get(guestId)?.name || '(読み込み中...)'
  }, [guestId, guestStore])

  const operatorName = authStore.user?.name || ''

  const isAssignedToMe = !!currentRoom?.user && currentRoom?.user === userId
  const isWaiting = !!currentRoom && !currentRoom.user
  const canSend = !!currentRoom && isAssignedToMe

  const wsStatusIcon =
    webSocketStore.status === 'connected' ? (
      <Circle className="h-2 w-2 fill-green-500 text-green-500" />
    ) : (
      <Circle className="h-2 w-2 fill-gray-400 text-gray-400" />
    )

  const handleLogout = () => {
    if (authStore.isSpoofing) return
    webSocketStore.disconnect()
    authStore.logout()
    navigate('/auth/login')
  }

  return (
    <div className="flex h-screen w-full overflow-hidden">
      <RoomSidebar selectedRoomId={roomStore.currentRoomId} onSelectRoom={(id) => void handleSelectRoom(id)} />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-[15px] font-bold">hogeAgent</span>
            {currentRoom ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarFallback>
                    <Identicon source={`g:${guestId || ''}`} size={24} />
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm truncate">{guestName}</span>
                {isWaiting && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">未対応</Badge>}
                {isAssignedToMe && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">対応中</Badge>}
              </>
            ) : (
              <span className="text-sm text-muted-foreground truncate">
                左のサイドバーからルームを選択してください
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {wsStatusIcon}
            {currentRoom && isWaiting && (
              <Button size="sm" onClick={() => void handleAssign()}>
                対応を開始
              </Button>
            )}
            {canSend && (
              <>
                <Button size="sm" variant="outline" onClick={() => void handleRelease()}>
                  対応を解除
                </Button>
                <Button size="sm" variant="secondary" onClick={() => void handleClose()}>
                  クローズ
                </Button>
              </>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 ml-1">
                  <Grid3x3 className="h-4 w-4" />
                  <span className="hidden sm:inline">hogeSeries</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>プロダクト一覧</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hogeSeriesProducts.map((product) => (
                  <DropdownMenuItem
                    key={product.name}
                    disabled={product.current}
                    onClick={() => (window.location.href = product.url)}
                    className="cursor-pointer"
                  >
                    <product.icon className="mr-2 h-4 w-4" />
                    <div className="flex-1">
                      <div className="font-medium">{product.name}</div>
                      <div className="text-xs text-muted-foreground">{product.description}</div>
                    </div>
                    {!product.current && <ExternalLink className="ml-2 h-3 w-3" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  disabled={authStore.isSpoofing}
                  onClick={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  ログアウト
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {!webSocketStore.isEnabled && (
          <div className="bg-blue-50 text-blue-800 text-xs px-4 py-1.5">
            リアルタイム接続が無効です ({config.chatWsUrl === '' ? 'WS 未設定' : '切断'}・{POLLING_INTERVAL_MS / 1000}秒間隔で同期中)
          </div>
        )}

        {currentRoom ? (
          <>
            <MessageList
              messages={roomStore.currentMessages}
              guestName={guestName}
              operatorName={operatorName}
              loading={roomStore.loadingMessages}
            />
            <MessageInput
              disabled={!canSend}
              disabledReason={
                currentRoom.state === 2
                  ? 'このルームはクローズされています'
                  : isWaiting
                    ? '「対応を開始」をクリックするとメッセージを送信できます'
                    : '他のオペレーターが対応中です'
              }
              sending={sending}
              uploading={uploading}
              onSend={(text) => void handleSend(text)}
              onImageSelected={(file) => void handleImageSelected(file)}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <MessageSquare className="h-8 w-8 mr-2 opacity-40" />
            ルームを選択してチャットを開始しましょう
          </div>
        )}
      </div>
    </div>
  )
})

export default ChatPage